import {
  skipToken,
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
  type UseMutationResult,
  type UseQueryOptions,
  type UseQueryResult,
} from "@tanstack/react-query";

import {
  createPropagation,
  deletePropagation,
  getPropagation,
  listPropagations,
  updatePropagation,
  updatePropagationItem,
  type ListPropagationsParams,
} from "./propagations";
import { streamPropagate, streamPublish } from "./propagationStream";

import {
  type CreatePropagationRequest,
  type ListPropagationsResponse,
  type Propagation,
  type PropagateProgress,
  type PropagateRequest,
  type PropagationItem,
  type PublishProgress,
  type PublishRequest,
  type UpdatePropagationItemRequest,
  type UpdatePropagationRequest,
} from "@/types/propagation";

export const propagationKeys = {
  all: ["propagation"] as const,
  /** Prefix that partial-matches every list query for the site, regardless of params. */
  propagationsList: (siteId: string) =>
    [...propagationKeys.all, "propagations", siteId] as const,
  propagations: (siteId: string, params: ListPropagationsParams = {}) =>
    [...propagationKeys.propagationsList(siteId), params] as const,
  /** Prefix that partial-matches every single-propagation detail for the site. */
  propagationDetail: (siteId: string) =>
    [...propagationKeys.all, "propagation", siteId] as const,
  propagation: (siteId: string, slug: string) =>
    [...propagationKeys.propagationDetail(siteId), slug] as const,
  propagateProgress: (siteId: string) =>
    [...propagationKeys.all, "propagate-progress", siteId] as const,
  publishProgress: (siteId: string) =>
    [...propagationKeys.all, "publish-progress", siteId] as const,
};

// ── Queries ───────────────────────────────────────────────────────────────────

export const propagationsQueryOptions = (
  siteId: string,
  params: ListPropagationsParams = {},
) =>
  ({
    queryKey: propagationKeys.propagations(siteId, params),
    queryFn: () => listPropagations(siteId, params),
    enabled: Boolean(siteId),
  }) satisfies Pick<
    UseQueryOptions<ListPropagationsResponse, Error>,
    "queryKey" | "queryFn" | "enabled"
  >;

export function usePropagations(
  siteId: string,
  params: ListPropagationsParams = {},
  options?: Omit<
    UseQueryOptions<ListPropagationsResponse, Error>,
    "queryKey" | "queryFn"
  >,
): UseQueryResult<ListPropagationsResponse, Error> {
  return useQuery<ListPropagationsResponse, Error>({
    ...propagationsQueryOptions(siteId, params),
    ...options,
  });
}

export const propagationQueryOptions = (siteId: string, slug: string) =>
  ({
    queryKey: propagationKeys.propagation(siteId, slug),
    queryFn: () => getPropagation(siteId, slug),
    enabled: Boolean(siteId && slug),
  }) satisfies Pick<
    UseQueryOptions<Propagation, Error>,
    "queryKey" | "queryFn" | "enabled"
  >;

export function usePropagation(
  siteId: string,
  slug: string,
  options?: Omit<UseQueryOptions<Propagation, Error>, "queryKey" | "queryFn">,
): UseQueryResult<Propagation, Error> {
  return useQuery<Propagation, Error>({
    ...propagationQueryOptions(siteId, slug),
    ...options,
  });
}

// ── Mutations ─────────────────────────────────────────────────────────────────

/**
 * POST /v1/{site_id}/propagations — creates a propagation. On success,
 * invalidates the propagations list so the new workflow appears.
 */
export function useCreatePropagation(
  siteId: string,
  options?: Omit<
    UseMutationOptions<Propagation, Error, CreatePropagationRequest>,
    "mutationFn" | "mutationKey"
  >,
): UseMutationResult<Propagation, Error, CreatePropagationRequest> {
  const queryClient = useQueryClient();
  return useMutation<Propagation, Error, CreatePropagationRequest>({
    mutationFn: (req) => createPropagation(siteId, req),
    ...options,
    mutationKey: ["createPropagation", siteId],
    onSuccess: (data, variables, onMutateResult, context) => {
      void queryClient.invalidateQueries({
        queryKey: propagationKeys.propagationsList(siteId),
      });
      options?.onSuccess?.(data, variables, onMutateResult, context);
    },
  });
}

/**
 * PATCH /v1/{site_id}/propagations/{slug} — updates a propagation. On success,
 * refreshes the cached single-propagation entry and invalidates the list.
 */
export function useUpdatePropagation(
  siteId: string,
  options?: Omit<
    UseMutationOptions<
      Propagation,
      Error,
      { slug: string; body: UpdatePropagationRequest }
    >,
    "mutationFn" | "mutationKey"
  >,
): UseMutationResult<
  Propagation,
  Error,
  { slug: string; body: UpdatePropagationRequest }
> {
  const queryClient = useQueryClient();
  return useMutation<
    Propagation,
    Error,
    { slug: string; body: UpdatePropagationRequest }
  >({
    mutationFn: ({ slug, body }) => updatePropagation(siteId, slug, body),
    ...options,
    mutationKey: ["updatePropagation", siteId],
    onSuccess: (data, variables, onMutateResult, context) => {
      queryClient.setQueryData(
        propagationKeys.propagation(siteId, data.slug),
        data,
      );
      void queryClient.invalidateQueries({
        queryKey: propagationKeys.propagationsList(siteId),
      });
      options?.onSuccess?.(data, variables, onMutateResult, context);
    },
  });
}

/**
 * DELETE /v1/{site_id}/propagations/{slug} — deletes a propagation. On success,
 * invalidates the propagations list.
 */
export function useDeletePropagation(
  siteId: string,
  options?: Omit<
    UseMutationOptions<void, Error, string>,
    "mutationFn" | "mutationKey"
  >,
): UseMutationResult<void, Error, string> {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (slug) => deletePropagation(siteId, slug),
    ...options,
    mutationKey: ["deletePropagation", siteId],
    onSuccess: (data, slug, onMutateResult, context) => {
      void queryClient.invalidateQueries({
        queryKey: propagationKeys.propagationsList(siteId),
      });
      options?.onSuccess?.(data, slug, onMutateResult, context);
    },
  });
}

type UpdatePropagationItemVariables = {
  itemId: string;
  body: UpdatePropagationItemRequest;
};

/**
 * PATCH /v1/{site_id}/propagations/{slug}/items/{item_id} — flips a single
 * item's acceptance. Optimistically patches the cached propagation (rolled back
 * on error) so the review grid reflects the choice immediately.
 */
export function useUpdatePropagationItem(
  siteId: string,
  slug: string,
  options?: Omit<
    UseMutationOptions<
      PropagationItem,
      Error,
      UpdatePropagationItemVariables,
      { previous?: Propagation }
    >,
    "mutationFn" | "mutationKey" | "onMutate"
  >,
): UseMutationResult<
  PropagationItem,
  Error,
  UpdatePropagationItemVariables,
  { previous?: Propagation }
> {
  const queryClient = useQueryClient();
  const queryKey = propagationKeys.propagation(siteId, slug);
  return useMutation<
    PropagationItem,
    Error,
    UpdatePropagationItemVariables,
    { previous?: Propagation }
  >({
    mutationFn: ({ itemId, body }) =>
      updatePropagationItem(siteId, slug, itemId, body),
    ...options,
    mutationKey: ["updatePropagationItem", siteId, slug],
    onMutate: async ({ itemId, body }) => {
      // Stop an in-flight poll from overwriting the optimistic value.
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<Propagation>(queryKey);
      if (previous) {
        queryClient.setQueryData<Propagation>(queryKey, {
          ...previous,
          items: previous.items.map((item) =>
            item.slug === itemId || item.id === itemId
              ? { ...item, is_accepted: body.is_accepted }
              : item,
          ),
        });
      }
      return { previous };
    },
    onError: (error, variables, onMutateResult, context) => {
      if (onMutateResult?.previous) {
        queryClient.setQueryData(queryKey, onMutateResult.previous);
      }
      options?.onError?.(error, variables, onMutateResult, context);
    },
    onSettled: (data, error, variables, onMutateResult, context) => {
      void queryClient.invalidateQueries({ queryKey });
      options?.onSettled?.(data, error, variables, onMutateResult, context);
    },
  });
}

// ── Streaming mutations + live progress ───────────────────────────────────────

/**
 * Reads the live propagate progress published by {@link useStreamPropagate}.
 * Returns undefined before the first frame and once the run settles. The value
 * is written imperatively via `setQueryData`; `skipToken` is the sanctioned
 * "never fetch" queryFn (a missing queryFn logs a dev console error on every
 * mount since react-query 5.90, even when the query is disabled).
 */
export function useStreamPropagateProgress(
  siteId: string,
): PropagateProgress | undefined {
  const { data } = useQuery<PropagateProgress | null>({
    queryKey: propagationKeys.propagateProgress(siteId),
    queryFn: skipToken,
  });
  // `null` is the "settled / cleared" sentinel (setQueryData ignores `undefined`).
  return data ?? undefined;
}

/**
 * POST /v1/stream/propagate — streams `PropagateProgress` frames. Bridges each
 * frame into the query cache (see {@link useStreamPropagateProgress}) so any
 * mounted component reads the same live value, then invalidates the propagations
 * list once the run settles.
 */
export function useStreamPropagate(
  siteId: string,
  options?: Omit<
    UseMutationOptions<PropagateProgress[], Error, PropagateRequest>,
    "mutationFn" | "mutationKey"
  >,
): UseMutationResult<PropagateProgress[], Error, PropagateRequest> {
  const queryClient = useQueryClient();
  return useMutation<PropagateProgress[], Error, PropagateRequest>({
    mutationFn: (req) =>
      streamPropagate(req, {
        onProgress: (progress) =>
          queryClient.setQueryData(
            propagationKeys.propagateProgress(siteId),
            progress,
          ),
      }),
    ...options,
    mutationKey: ["streamPropagate", siteId],
    onSettled: (data, error, variables, onMutateResult, context) => {
      // Clear the shared progress once the run finishes.
      queryClient.setQueryData(propagationKeys.propagateProgress(siteId), null);
      // A propagate run mutates both the list and the affected propagation's
      // detail (status/items). Invalidate both prefixes so neither cache shows
      // stale state (invalidate the detail prefix rather than a single key).
      void queryClient.invalidateQueries({
        queryKey: propagationKeys.propagationsList(siteId),
      });
      void queryClient.invalidateQueries({
        queryKey: propagationKeys.propagationDetail(siteId),
      });
      options?.onSettled?.(data, error, variables, onMutateResult, context);
    },
  });
}

/**
 * Reads the live publish progress published by {@link useStreamPublish}. Same
 * contract as {@link useStreamPropagateProgress}.
 */
export function useStreamPublishProgress(
  siteId: string,
): PublishProgress | undefined {
  const { data } = useQuery<PublishProgress | null>({
    queryKey: propagationKeys.publishProgress(siteId),
    queryFn: skipToken,
  });
  return data ?? undefined;
}

/**
 * POST /v1/stream/publish — streams `PublishProgress` frames. Same cache-bridging
 * pattern as {@link useStreamPropagate}.
 */
export function useStreamPublish(
  siteId: string,
  options?: Omit<
    UseMutationOptions<PublishProgress[], Error, PublishRequest>,
    "mutationFn" | "mutationKey"
  >,
): UseMutationResult<PublishProgress[], Error, PublishRequest> {
  const queryClient = useQueryClient();
  return useMutation<PublishProgress[], Error, PublishRequest>({
    mutationFn: (req) =>
      streamPublish(req, {
        onProgress: (progress) =>
          queryClient.setQueryData(
            propagationKeys.publishProgress(siteId),
            progress,
          ),
      }),
    ...options,
    mutationKey: ["streamPublish", siteId],
    onSettled: (data, error, variables, onMutateResult, context) => {
      queryClient.setQueryData(propagationKeys.publishProgress(siteId), null);
      // A publish run mutates the affected propagation's detail (status/items)
      // and its position in status-filtered lists. The request no longer names a
      // slug, so invalidate both detail and list prefixes for the site.
      void queryClient.invalidateQueries({
        queryKey: propagationKeys.propagationDetail(siteId),
      });
      void queryClient.invalidateQueries({
        queryKey: propagationKeys.propagationsList(siteId),
      });
      options?.onSettled?.(data, error, variables, onMutateResult, context);
    },
  });
}
